import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import { getCategoryById } from "@/mocks/categories";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");
  const category = article ? getCategoryById(article.categoryId) : null;
  const relatedArticles = article ? getRelatedArticles(article.id, 3) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20">
          <section className="px-4 md:px-6 lg:px-10 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              该文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              ...(category
                ? [
                    {
                      label: category.name,
                      href: `/category/${category.slug}`,
                    },
                  ]
                : []),
              { label: article.title },
            ]}
          />
        </div>

        <article className="px-4 md:px-6 lg:px-10 py-10">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-block px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2 py-0.5 text-xs bg-background-100 text-foreground-600 rounded whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:font-heading prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900 prose-strong:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 lg:px-10 py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.imageUrl,
            "datePublished": article.publishDate,
            "author": {
              "@type": "Organization",
              "name": "女王调教指南",
            },
            "publisher": {
              "@type": "Organization",
              "name": "女王调教指南",
            },
            "url": window.location.href,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": window.location.origin,
                },
                ...(category
                  ? [
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": category.name,
                        "item": `${window.location.origin}/category/${category.slug}`,
                      },
                    ]
                  : []),
                {
                  "@type": "ListItem",
                  "position": category ? 3 : 2,
                  "name": article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}